import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { InstallBanner } from "../components/InstallBanner";
import { IosInstallHint } from "../components/IosInstallHint";
import { Topbar } from "../components/Topbar";
import { APP_NAME } from "../constants/brand";
import { INTEGRATIONS_ENABLED } from "../constants/features";
import { useServiceWorker } from "../hooks/useServiceWorker";
import { useAuth } from "../services/auth-context";
import { PageProvider } from "../services/page-context";
import { useSubscription } from "../services/subscription-context";
import { useCompanies } from "../services/company-context";

type NavIconName = "dashboard" | "setup" | "clients" | "calendar" | "shield" | "file" | "log" | "settings" | "integrations" | "card" | "billing" | "chat";

const NAV_ICON_PATHS: Record<NavIconName, string> = {
  dashboard: "M3 3h7v7H3zm11 0h7v7h-7zM3 14h7v7H3zm11 0h7v7h-7z",
  setup: "M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  clients: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z",
  calendar: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8",
  log: "M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z",
  settings:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
  integrations: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
  card: "M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM1 10h22",
  billing: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M12 18v-6M9 15h6",
  chat: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
};

function NavIcon({ name }: { name: NavIconName }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d={NAV_ICON_PATHS[name]} />
    </svg>
  );
}

const APP_NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: "dashboard" as NavIconName },
  { to: "/onboarding", label: "Configurazione", icon: "setup" as NavIconName },
  { to: "/clients", label: "Clienti", icon: "clients" as NavIconName },
  { to: "/invoices", label: "Scadenze", icon: "calendar" as NavIconName },
  { to: "/policies", label: "Policy", icon: "shield" as NavIconName },
  { to: "/templates", label: "Template", icon: "file" as NavIconName },
  { to: "/conversations", label: "Conversazioni", icon: "chat" as NavIconName },
  { to: "/logs", label: "Log invii", icon: "log" as NavIconName },
  // Gestionali integrations hidden until the feature is ready (see constants/features.ts)
  ...(INTEGRATIONS_ENABLED
    ? [{ to: "/integrations", label: "Integrazioni", icon: "integrations" as NavIconName }]
    : []),
];

const STUDIO_NAV_ITEM = { to: "/companies", label: "Mandanti", icon: "clients" as NavIconName };

const SETTINGS_NAV_ITEMS = [
  { to: "/settings", label: "Impostazioni", icon: "settings" as NavIconName },
];

const BILLING_NAV_ITEMS = [
  { to: "/plans", label: "Piani", icon: "card" as NavIconName },
  { to: "/billing", label: "Fatturazione", icon: "billing" as NavIconName },
];

export function AppShell() {
  const { tokens } = useAuth();
  const { status } = useSubscription();
  const { companies, selectedId, isStudio, select: selectCompany } = useCompanies();
  const navigate = useNavigate();
  const location = useLocation();
  const canAccessApp = Boolean(status?.has_active_subscription && status?.app_access_allowed);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useServiceWorker(tokens?.access_token);

  // Close drawer on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <PageProvider>
    <div className="app-shell">
      {/* Overlay */}
      {mobileMenuOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      <aside className={`sidebar ${mobileMenuOpen ? "sidebar--open" : ""}`}>
        {/* Mobile close button */}
        <button
          type="button"
          className="sidebar-close-btn"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Chiudi menu"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="brand-block">
          <div className="brand-block-top">
            <div className="brand-icon">I</div>
            <span className="brand-name">{APP_NAME}</span>
          </div>
          <div className="brand-block-info">
            <p className="brand-workspace-name">
              {tokens?.workspace_id ? `#${tokens.workspace_id.slice(0, 8)}` : ""}
            </p>
            {status?.workspace_status && (
              <span className="workspace-status-chip">
                <span className="workspace-status-dot" />
                {status.workspace_status === "active" ? "Attivo" : status.workspace_status}
              </span>
            )}
          </div>
        </div>

        {isStudio && (
          <div className="company-switcher">
            <label className="company-switcher-label" htmlFor="company-switcher">
              Mandante
            </label>
            <select
              id="company-switcher"
              className="company-switcher-select"
              value={selectedId ?? ""}
              onChange={(event) => selectCompany(event.target.value || null)}
            >
              <option value="">Tutte le mandanti</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <nav className="sidebar-nav">
          {canAccessApp && (
            <>
              {isStudio && (
                <NavLink
                  key={STUDIO_NAV_ITEM.to}
                  to={STUDIO_NAV_ITEM.to}
                  className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <NavIcon name={STUDIO_NAV_ITEM.icon} />
                  <span>{STUDIO_NAV_ITEM.label}</span>
                </NavLink>
              )}
              {APP_NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                >
                  <NavIcon name={item.icon} />
                  {item.label}
                </NavLink>
              ))}
              <div className="nav-separator" />
              <span className="nav-section-label">Gestione</span>
              {SETTINGS_NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                >
                  <NavIcon name={item.icon} />
                  {item.label}
                </NavLink>
              ))}
              <div className="nav-separator" />
              <span className="nav-section-label">Abbonamento</span>
            </>
          )}
          {BILLING_NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
            >
              <NavIcon name={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div style={{ marginTop: "auto", display: "grid", gap: 6 }}>
          {tokens?.is_superadmin && (
            <div className="role-toggle">
              <button className="role-toggle-btn" type="button" onClick={() => navigate("/admin")}>Admin</button>
              <button className="role-toggle-btn active" type="button">App</button>
            </div>
          )}
          {tokens?.is_consultant && (
            <div className="role-toggle">
              <button className="role-toggle-btn" type="button" onClick={() => navigate("/studio")}>Studio</button>
              <button className="role-toggle-btn active" type="button">App</button>
            </div>
          )}
          <div className="app-shell-legal-links">
            <NavLink to="/privacy">Privacy</NavLink>
            <NavLink to="/terms">Termini</NavLink>
          </div>
        </div>
      </aside>

      <div className="main-column">
        {/* Impersonation banner */}
        {tokens?.access_token && (() => {
          try {
            const payload = JSON.parse(atob(tokens.access_token.split(".")[1]));
            if (payload.impersonating) {
              const expiresAt = new Date(payload.exp * 1000);
              return (
                <div className="stat-card stat-warning" style={{
                  borderRadius: 0, textAlign: "center", padding: "10px 16px",
                  borderBottom: "2px solid var(--warning)",
                }}>
                  <p style={{ margin: 0 }}>Sessione di sola lettura — Scade alle {expiresAt.toLocaleTimeString("it-IT")}</p>
                </div>
              );
            }
          } catch { /* ignore decode errors */ }
          return null;
        })()}

        {/* Consultant viewer banner */}
        {tokens?.is_consultant && tokens?.workspace_id && (() => {
          try {
            const payload = JSON.parse(atob(tokens.access_token.split(".")[1]));
            if (payload.role === "viewer") {
              return (
                <div className="stat-card" style={{
                  borderRadius: 0, textAlign: "center", padding: "10px 16px",
                  borderBottom: "2px solid var(--brand)", background: "var(--surface-soft)",
                }}>
                  <p style={{ margin: 0 }}>
                    Vista consulente (sola lettura) —{" "}
                    <a href="/studio" style={{ fontWeight: 600, textDecoration: "underline" }}>Torna a Studio →</a>
                  </p>
                </div>
              );
            }
          } catch { /* ignore */ }
          return null;
        })()}

        {/* Trial banner */}
        {status?.is_trial && (
          <div className="stat-card stat-warning" style={{
            borderRadius: 0, textAlign: "center", padding: "10px 16px",
            borderBottom: "2px solid var(--warning)",
          }}>
            <p style={{ margin: 0 }}>
              Stai usando il periodo di prova gratuito.{" "}
              {status.trial_days_remaining !== null && status.trial_days_remaining !== undefined
                ? `${status.trial_days_remaining} giorni rimasti. `
                : ""}
              <a href="/plans" style={{ fontWeight: 600, textDecoration: "underline" }}>
                Passa a un piano
              </a>
            </p>
          </div>
        )}

        <InstallBanner />
        <IosInstallHint />
        <Topbar canAccessApp={canAccessApp} onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} menuOpen={mobileMenuOpen} />

        <main className="page-content" id="main-content">
          <Outlet />
        </main>
      </div>
    </div>
    </PageProvider>
  );
}
